// import type { LanguageInfo } from "@/utils/types"
import type { LanguageCode } from "@/utils/types"
import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export const languages: Record<LanguageCode, { name: string; emoji: string }> = {
  ar: { name: "Arabic", emoji: "🇸🇦" },
  de: { name: "German", emoji: "🇩🇪" },
  en: { name: "English", emoji: "🇬🇧" },
  es: { name: "Spanish", emoji: "🇪🇸" },
  fr: { name: "French", emoji: "🇫🇷" },
  it: { name: "Italian", emoji: "🇮🇹" },
  ja: { name: "Japanese", emoji: "🇯🇵" },
  ko: { name: "Korean", emoji: "🇰🇷" },
  pt: { name: "Portuguese", emoji: "🇵🇹" },
  ru: { name: "Russian", emoji: "🇷🇺" },
  tr: { name: "Turkish", emoji: "🇹🇷" },
  zh: { name: "Mandarin", emoji: "🇨🇳" },
}

export const languageList: LanguageCode[] = ["ar", "de", "en", "es", "fr", "it", "ja", "ko", "pt", "ru", "tr", "zh"]

export const nonAlphabetLanguages: LanguageCode[] = ["ja", "ko", "zh", "ar"]

/**
 * removes all accents (old)
 */
// export function accentCleanse(input: string, strict?: boolean): string {
// 	if (strict) return input;
// 	return input
// 	  .normalize('NFD')
// 	  .replace(/[\u0300-\u036f]/g, '')
// 	  .replace(/ø/gi, 'o')
// 	  .replace(/æ/gi, 'a')
// 	  .replace(/œ/gi, 'o')
// 	  .replace(/ß/g, 'ss');
//   }

const accentMap: Record<string, string> = {
  ø: "o",
  Ø: "O",
  æ: "a",
  Æ: "A",
  œ: "o",
  Œ: "O",
  ß: "s",
  ł: "l",
  Ł: "L",
  đ: "d",
  Đ: "D",
  ð: "d",
  Ð: "D",
  þ: "th",
  Þ: "Th",
  // (add more weird characters if needed)
}

// export function mapCodeToChar(code: string): string {
//   // Letter keys
//   if (/^Key[A-Z]$/.test(code)) {
//     return code.slice(3).toLowerCase() // "KeyK" → "k"
//   }

//   // Number keys (top row, not numpad)
//   if (/^Digit[0-9]$/.test(code)) {
//     return code.slice(5) // "Digit2" → "2"
//   }

//   return "" // ignore everything else
// }

// sprout || wand sparkles || lollipop || candy
export function accentCleanse(input: string, strict?: boolean): string {
  if (strict || !input) return input

  return input
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .split("")
    .map(char => accentMap[char] ?? char)
    .join("")
}

// export function calculateWPM(timeSeconds: number, charCount: number): number {
//   if (timeSeconds <= 0) return 0
//   const wordCount = charCount / 5
//   return Math.round((wordCount / timeSeconds) * 60)
// }

export function calculateWPM(timeSeconds: number, charCount: number, language: string, strictMode: boolean): number {
  if (timeSeconds <= 0) return 0

  if (strictMode) language = "roman" // treat strict mode as roman alphabet

  const divisorMap: Record<string, number> = {
    roman: 5,
    ja: 1.8,
    zh: 1.8,
    ko: 2.8,
    ar: 4,
    default: 5,
  }

  const divisor = divisorMap[language] ?? divisorMap.default
  const wordCount = charCount / divisor

  return Math.round((wordCount / timeSeconds) * 60)
}

export function calculateAccuracy(
  foreignWords: string[],
  inputWords: string[],
  wordIndex: number,
  strictMode: boolean,
): number {
  const foreignWord = accentCleanse(foreignWords[wordIndex], strictMode)
  const inputWord = accentCleanse(inputWords[wordIndex], strictMode)

  const correctLetters = foreignWord.split("").filter((letter, index) => letter === inputWord[index]).length
  const accuracy = (correctLetters / foreignWord.length) * 100

  return Math.round(accuracy)
}

export function dateToSeconds(date: number) {
  return Number((date / 1000).toString().split(".")[0])
}

export const baseUrl = import.meta.env.VITE_API_URL as string

export function decimalToPercent(decimal: number): string {
  // return 0 percent if not valid number
  if (isNaN(decimal) || decimal < 0 || !decimal) return "0%"
  return Math.round(decimal * 100).toFixed(0) + "%"
}

/**
 * Truncates a number to a specified number of decimal places.
 * @param value
 * @param decimals (default: 2)
 * @returns Truncated number
 */
export function truncateDecimals(value: number, decimals = 2): number {
  const factor = Math.pow(10, decimals)
  return Math.floor(value * factor) / factor
}

// chatGPT function
export function isLikelyHuman(timestamps: number[]): boolean {
  console.log("timestamps: ", timestamps)
  if (timestamps.length < 30) return true // not enough data to judge

  const intervals = timestamps.slice(1).map((timestamp, i) => timestamp - timestamps[i])

  const avg = intervals.reduce((sum, val) => sum + val, 0) / intervals.length
  const stddev = Math.sqrt(intervals.reduce((sum, val) => sum + (val - avg) ** 2, 0) / intervals.length)

  //   const min = Math.min(...intervals)
  const max = Math.max(...intervals)

  // Heuristic thresholds (tweak based on real user data)
  const tooFast = avg < 60 // < 60 ms average = suspiciously fast
  const tooConsistent = stddev < 15 // almost no variability
  //   const minTooLow = min < 30 // below mechanical threshold (causes false positives)
  const maxTooHigh = max > 5000 // very long pause? could mean irregular logging

  const flagged =
    (tooFast && tooConsistent) ||
    //   || minTooLow
    maxTooHigh

  return !flagged
}
